import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { httpsCallable } from 'firebase/functions'
import type { Functions, HttpsCallable } from 'firebase/functions'
import type { CallableEnvelope } from '@gh/common/callable-client'
import { isFirebaseFunctionError } from '@gh/common/models'
import { redactUnknownPiiSafe, scrubSentryEvent } from '@gh/common/utils'
import { pwaConfig } from './pwaTools'
import { isSentryOutboundPiiScrubEnabled } from './sentryPiiScrubSwitch'
import {
  isIndexedDbConnectionLostError,
  isIndexedDbMissingObjectStoreError,
} from './indexedDbTransientErrors'
import { isExpectedAudioCaptureNotReadableError } from './mediaDeviceExpectedErrors'
import { isChunkLoadError } from './versionCheck'
import { getErrorCode, getErrorMessage } from './errorUtils'
import { shouldDropSentryEventFromLocalDevPage } from './sentryLocalDevUrl'
import { isStoreReloadRateLimitError } from './storeReloadRateLimit'
import {
  isExpectedLiveClassBrowserPolicyError,
  isLiveClassSentryEvent,
} from './liveClassBrowserPolicyErrors'

/**
 * Callable `zoom_queueRegistration` returns these for normal/expected paths or transient
 * backend issues; callers handle them (e.g. fallback registration) — do not report to Sentry.
 */
function shouldSuppressSentryForZoomQueueRegistration(error: unknown): boolean {
  const code = getErrorCode(error)
  const message = getErrorMessage(error) ?? ''

  if (code === 'functions/permission-denied' && message.includes('Active subscription required')) {
    return true
  }
  if (code === 'functions/already-exists') {
    return true
  }
  if (code === 'functions/internal') {
    return true
  }
  return false
}

function scrubOutboundEvent<T>(event: T): T {
  return isSentryOutboundPiiScrubEnabled() ? scrubSentryEvent(event) : event
}

export function initializeSentry(app: App, router: Router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  /**
   * Do not send to Sentry from:
   * - Vite dev server (`import.meta.env.DEV`) — any profile.
   * - `VITE_LOCAL_DEVELOPMENT === "true"` (env-cmd `local` profile: emulators/localhost), including
   *   production builds and `vite preview` baked with that flag — unlike `DEV`, this is false for
   *   hosted **development** project builds that use the `development` profile without `local`.
   */
  const isLocalFrontendContext =
    import.meta.env.DEV || import.meta.env.VITE_LOCAL_DEVELOPMENT === 'true'
  const enabled = Boolean(dsn) && !isLocalFrontendContext
  const traceTargetsConf = import.meta.env.VITE_SENTRY_TRACING_TARGETS
  Sentry.init({
    app,
    dsn: enabled ? dsn : undefined,
    enabled,
    environment: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    release: __APP_VERSION__,
    sendDefaultPii: false,
    integrations: [
      // Prefer route `name` (e.g. admin-customer-profile) over raw paths that embed customer ids.
      Sentry.browserTracingIntegration({ router, routeLabel: 'name' }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.zodErrorsIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0,
    tracePropagationTargets: traceTargetsConf ? traceTargetsConf.split(' ') : [],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Logs
    enableLogs: true,
    initialScope: {
      tags: {
        displayMode: pwaConfig.displayMode,
        browserInfoName: pwaConfig.browserInfo.name,
        browserInfoOs: pwaConfig.browserInfo.os,
      },
    },
    beforeSend(event, hint) {
      // Do not send events from the local Vite dev server; they share the same DSN as deployed
      // builds and pollute production monitoring (e.g. FRONTEND-JQ from localhost + dev Firebase).
      if (shouldDropSentryEventFromLocalDevPage(event)) {
        return null
      }

      if (event.tags?.chunk_error_handled === 'real-issue') {
        return scrubOutboundEvent(event)
      }

      const checkFiltered = (err: unknown) =>
        isChunkLoadError(err) ||
        isIndexedDbConnectionLostError(err) ||
        isIndexedDbMissingObjectStoreError(err) ||
        isExpectedAudioCaptureNotReadableError(err) ||
        (isLiveClassSentryEvent(event) && isExpectedLiveClassBrowserPolicyError(err))

      // Store reload rate limit: reported explicitly in sentryStoreReload.ts (do not drop here).
      if (checkFiltered(hint.originalException)) {
        return null
      }

      const hasFilteredException =
        event.exception?.values?.some((v) => {
          const frame = { name: v.type, message: v.value }
          return checkFiltered(frame) && !isStoreReloadRateLimitError(frame)
        }) ?? false
      if (
        hasFilteredException ||
        (checkFiltered(event.message) && !isStoreReloadRateLimitError(event.message))
      ) {
        return null
      }

      return scrubOutboundEvent(event)
    },
    beforeSendTransaction(event) {
      if (shouldDropSentryEventFromLocalDevPage(event)) {
        return null
      }
      return scrubOutboundEvent(event)
    },
  })
}

/** True when `data` is a gateway wire envelope (`{ action, data? }` only). */
export function isCallableEnvelopeShape(data: unknown): data is CallableEnvelope {
  if (typeof data !== 'object' || data === null) {
    return false
  }
  const record = data as Record<string, unknown>
  if (typeof record.action !== 'string' || record.action.length === 0) {
    return false
  }
  return Object.keys(record).every((key) => key === 'action' || key === 'data')
}

function scrubCallableRequestData(data: unknown): unknown {
  return isSentryOutboundPiiScrubEnabled() ? redactUnknownPiiSafe(data) : data
}

/** Sentry tags/context for callable errors; gateway envelopes get route-level action tags. */
export function getCallableSentryFields(
  gatewayName: string,
  requestData: unknown,
): {
  requestDataForSentry: unknown
  tags: Record<string, string>
  firebaseContext: Record<string, unknown>
} {
  if (isCallableEnvelopeShape(requestData)) {
    const payload = requestData.data
    const scrubbedPayload = scrubCallableRequestData(payload)
    return {
      requestDataForSentry: scrubbedPayload,
      tags: {
        'callable.gateway': gatewayName,
        'callable.action': requestData.action,
      },
      firebaseContext: {
        function_name: gatewayName,
        callable_gateway: gatewayName,
        callable_action: requestData.action,
        request_data: scrubbedPayload,
      },
    }
  }

  const scrubbed = scrubCallableRequestData(requestData)
  return {
    requestDataForSentry: scrubbed,
    tags: {},
    firebaseContext: {
      function_name: gatewayName,
      request_data: scrubbed,
    },
  }
}

/**
 * Creates a Sentry-traced httpsCallable function that automatically captures errors
 * @param functions - Firebase Functions instance
 * @param name - Function name
 * @returns Wrapped httpsCallable function with Sentry error capture
 */
export function traceHttpsCallable<TRequest = unknown, TResponse = unknown>(
  functions: Functions,
  name: string,
): HttpsCallable<TRequest, TResponse> {
  const callable = httpsCallable<TRequest, TResponse>(functions, name)

  const wrappedFunction = async (data?: TRequest) => {
    try {
      const result = await callable(data)
      return result
    } catch (error: unknown) {
      if (
        name === 'zoom_queueRegistration' &&
        shouldSuppressSentryForZoomQueueRegistration(error)
      ) {
        throw error
      }

      // Check if the error has an errorInstanceId from the backend
      const errorInstanceId = isFirebaseFunctionError(error)
        ? error.details?.errorInstanceId
        : undefined

      const sentryFields = getCallableSentryFields(name, data)

      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          'api.source': 'on-call-wrapper',
          'function.name': name,
          'backend.eventId': errorInstanceId,
          ...sentryFields.tags,
        },
        extra: {
          'request.data': sentryFields.requestDataForSentry,
          'function.name': name,
          ...(sentryFields.tags['callable.action']
            ? { 'callable.action': sentryFields.tags['callable.action'] }
            : {}),
        },
        contexts: {
          firebase: sentryFields.firebaseContext,
        },
      })

      // Re-throw the error so the calling code can handle it
      throw error
    }
  }

  // Add the stream property to match HttpsCallable interface
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(wrappedFunction as any).stream = callable.stream

  return wrappedFunction as HttpsCallable<TRequest, TResponse>
}
